import { RootState } from "app/store";
import { EControlType } from "components/ControlWrapper";
import { FieldsToLookupByMasterAttributeId, IFlexibleFieldWithAttributes, calcFieldName, friendlyValue } from "global/Application";
import { AppDataSource } from "global/DataSources/AppDataSource";
import Formatters from "global/Utility/Formatters";
import { ArrayToLookup } from "global/Utility/TSFunctions";
import {
    IBenefit as FAM_IBenefit,
    IGroupConfigurationConfig as FAM_IGroupConfigurationConfig,
    IGroupConfigurationData as FAM_IGroupConfigurationData,
    IGroupNumber as FAM_IGroupNumber,
    IPlan as FAM_IPlan
} from "model/FlexibleAttributeModel";
import { EColumnExtractDataTypes } from "./EColumnExtractDataTypes";
import { IColumnExtract } from "./IColumnExtract.type";
import { IDatasetExtract } from "./IDatasetExtract";
import { IRowExtract } from "./IRowExtract.type";
import { ExcelStyles } from "./ExcelStyles";
import * as XRM from 'model/CRM';

// TODO advanced settings implemntation
// TODO add snapshot row name
// TODO add tab/section

interface IPlanComparisonRowExtract extends IRowExtract {
    [key: string]: string | undefined | { value: string, format: string } | any;
    _config?: IFlexibleFieldWithAttributes | undefined;
    _category?: string | undefined;
    _property?: IPropertyAttribute<any> | undefined;
    label: string | undefined;
}

interface IPropertyAttribute<T> {
    index: number;
    logicalName: keyof T;
    label: string;
    type: EControlType;
    defaultValue: string;
    customMap?: (value: T) => string;
}

interface IPlanComparisonSheetData {
    rows: IPlanComparisonRowExtract[];
    rowsLookup: Record<string, IPlanComparisonRowExtract>;
    columns: Record<string, IColumnExtract>;
    categoryName: string;
}

export interface IPlanComparisonOptions {
    ignoreEmptyFields: boolean;
    showOnlyChanges: boolean;
}

const changeFormat = {
    format: { styleId: 'yellow' },
    value: 'changed',
};

const unchanged = 'unchanged';

// Plan properties configuration
const planProperties: Record<string, IPropertyAttribute<FAM_IPlan>> = {
    'name': {
        index: 0,
        logicalName: 'name',
        label: 'Name',
        type: EControlType.text,
        defaultValue: '-',
    },
    'deactivated': {
        index: 1,
        logicalName: 'deactivated',
        label: 'Deactivated',
        type: EControlType.boolean,
        defaultValue: 'false',
    },
};

// Utility Functions
const removeNonStandardCharacters = (value: string): string => {
    return value.replace(/[^a-zA-Z0-9 ]/g, "").trim();
};

interface IPlanComparisonCategorizedData {
    [key: string]: IPlanComparisonSheetData;
    planData: IPlanComparisonSheetData;
    groupNumbersData: IPlanComparisonSheetData;
}

export interface IPlanComparisonGroupConfigurationData {
    snapshot: XRM.bcbs_groupconfigurationsnapshot,
    json: Pick<FAM_IGroupConfigurationData, 'plans' | 'benefits' | 'feesAndPremiums' | 'groupStructure' | 'name'>,
    options: IPlanComparisonOptions,
}

export default async function PlanComparisonReportGeneration(
    state: RootState,
    dataSource: AppDataSource,
    groupConfigurations: IPlanComparisonGroupConfigurationData[],
): Promise<any> {
    const config = state.data.config;
    
    // Initialize plan overview category
    const planData: IPlanComparisonSheetData = {
        rows: [],
        rowsLookup: {},
        columns: {},
        categoryName: 'Plan'
    };
    addBaseColumns(planData);


    // Initialize group numbers category
    const groupNumbersData: IPlanComparisonSheetData = {
        rows: [],
        rowsLookup: {},
        columns: {},
        categoryName: 'Group Numbers'
    };
    addBaseColumns(groupNumbersData);

    const categorizedData: IPlanComparisonCategorizedData = {
        planData: planData,
        groupNumbersData: groupNumbersData,
    };

    // Process all plans and categorize data
    await processPlansData(groupConfigurations, config, categorizedData);

    // Generate final dataset with multiple tabs
    return await generateCategorizedDataset(
        categorizedData,
        dataSource,
        groupConfigurations?.[0]?.options ?? {
            ignoreEmptyFields: false,
            showOnlyChanges: false,
        }
    );
}

// Core data processing functions
async function processPlansData(
    groupConfigurations: IPlanComparisonGroupConfigurationData[],
    config: FAM_IGroupConfigurationConfig,
    categorizedData: IPlanComparisonCategorizedData,
): Promise<void> {

    // Process each group configuration
    for (const groupConfiguration of groupConfigurations) {
        for (const plan of groupConfiguration.json.plans) {
            // Add plan columns to all categories
            const columnName = addPlanColumn(plan, categorizedData);

            // Process plan properties (goes to plan overview)
            await processPlanProperties(groupConfiguration, plan, columnName, categorizedData.planData, planProperties);

            // Process plan fields (goes to plan overview)
            await processEntityFields(plan, columnName, plan as any, categorizedData.planData, config, "plan");

            // Process benefits by category
            if (groupConfiguration.json.benefits) {
                await processBenefits(plan, columnName, groupConfiguration.json.benefits, config, categorizedData);
            }

            // Process fees and premiums
            if (groupConfiguration.json.feesAndPremiums) {
                await processFeesAndPremiums(plan, columnName, groupConfiguration.json.feesAndPremiums, config, categorizedData);
            }

            // Process group numbers (goes to group numbers tab)
            if (groupConfiguration.json.groupStructure.groupNumbers) {
                await processGroupNumbers(plan, columnName, groupConfiguration.json.groupStructure.groupNumbers, categorizedData.groupNumbersData);
            }
        }
    }
}

function addPlanColumn(plan: FAM_IPlan, categorizedData: IPlanComparisonCategorizedData): string {
    let columnName = removeNonStandardCharacters(plan.name!);

    // Check for duplicate names across all categories and adjust if needed
    const allColumns = Array.from(Object.values(categorizedData)).flatMap(data => Object.values(data.columns));
    const existingCount = allColumns.filter(col => col.fieldName.startsWith(columnName)).length;

    if (existingCount > 0) {
        columnName = `${columnName} (${existingCount})`;
    }

    // Add column to all categories
    Object.values(categorizedData).forEach(data => {
        data.columns[columnName] = {
            attributeId: '',
            order: 0,
            width: 35,
            index: Object.keys(data.columns).length,
            fieldName: columnName,
            hidden: false,
            name: columnName,
            dataType: EColumnExtractDataTypes.Default,
        };
    });

    return columnName;
}

async function processPlanProperties(
    groupConfiguration: IPlanComparisonGroupConfigurationData,
    plan: FAM_IPlan,
    columnName: string,
    data: IPlanComparisonSheetData,
    planProperties: Record<string, IPropertyAttribute<FAM_IPlan>>
): Promise<void> {
    const prevKeyParts = ["plan", "properties"];

    {
        const key = 'plan/snapshot';
        let row = data.rowsLookup[key];
        if (!row) {
            row = {
                label: 'Snapshot',
                namespace: 'snapshot',
                _index: data.rows.length,
                _category: 'plan-overview'
            };
            data.rows.push(row);
            data.rowsLookup[key] = row;
        }
        row[columnName] = groupConfiguration.snapshot.bcbs_name!;
    }

    Object.values(planProperties).forEach(property => {
        const keyParts: string[] = [...prevKeyParts, property.logicalName as string];
        const key = keyParts.join('/');

        let row = data.rowsLookup[key];
        if (!row) {
            row = {
                label: property.label,
                namespace: prevKeyParts.join('/'),
                _index: data.rows.length,
                _category: 'plan-overview'
            };
            data.rows.push(row);
            data.rowsLookup[key] = row;
        }

        // Set default value
        row[columnName] = property.defaultValue;

        // Apply custom mapping or type-specific formatting
        if (property.customMap) {
            row[columnName] = property.customMap(plan);
        } else {
            switch (property.type) {
                case EControlType.boolean:
                    row[columnName] = plan[property.logicalName] === true ? "yes" :
                        plan[property.logicalName] === false ? "no" :
                            property.defaultValue;
                    break;
                case EControlType.wholeNumber:
                    if (plan[property.logicalName] !== undefined && plan[property.logicalName] !== null) {
                        row[columnName] = plan[property.logicalName]?.toString();
                    }
                    break;
                case EControlType.currency:
                    if (plan[property.logicalName] !== undefined && plan[property.logicalName] !== null) {
                        row[columnName] = Formatters.ToCurrencyUSD(plan[property.logicalName] as any as number);
                    }
                    break;
                case EControlType.text:
                    if (plan[property.logicalName] !== undefined && plan[property.logicalName] !== null) {
                        row[columnName] = plan[property.logicalName] as string;
                    }
                    break;
                default:
                    throw new Error(`Unknown property type ${property.type}`);
            }
        }
    });
}


async function processBenefits(
    plan: FAM_IPlan,
    columnName: string,
    benefits: FAM_IBenefit[],
    config: any,
    categorizedData: IPlanComparisonCategorizedData
): Promise<void> {
    // Group benefits by category
    const benefitsByCategory = benefits.reduce((acc, benefit) => {
        const category = benefit.bcbs_config_categoryid
            ? config.cache.map_bcbs_config_category[benefit.bcbs_config_categoryid!]
            : null;

        const categoryName = category?.bcbs_name || 'Uncategorized Benefits';
        const categoryKey = `benefit-${removeNonStandardCharacters(categoryName).toLowerCase()}`;

        if (!acc[categoryKey]) {
            acc[categoryKey] = {
                benefits: [],
                categoryName: `Benefits - ${categoryName}`
            };
        }
        acc[categoryKey].benefits.push(benefit);
        return acc;
    }, {} as Record<string, { benefits: FAM_IBenefit[], categoryName: string }>);

    // Process each category
    for (const [categoryKey, categoryData] of Object.entries(benefitsByCategory)) {
        // Ensure category exists
        if (!categorizedData[categoryKey]) {
            const data: IPlanComparisonSheetData = {
                rows: [],
                rowsLookup: {},
                columns: {},
                categoryName: categoryData.categoryName
            };
            addBaseColumns(data);
            categorizedData[categoryKey] = data;
        }

        const data = categorizedData[categoryKey]!;

        // Add plan column if not exists
        if (!data.columns[columnName]) {
            data.columns[columnName] = {
                attributeId: '',
                order: 0,
                width: 35,
                index: Object.keys(data.columns).length,
                fieldName: columnName,
                hidden: false,
                name: columnName,
                dataType: EColumnExtractDataTypes.Default,
            };
        }

        // Process benefits in this category
        for (const benefit of categoryData.benefits) {
            await processEntityFields(plan, columnName, benefit, data, config, "benefit");
        }
    }
}

async function processFeesAndPremiums(
    plan: FAM_IPlan,
    columnName: string,
    feesAndPremiums: any[],
    config: any,
    categorizedData: IPlanComparisonCategorizedData,
): Promise<void> {
    const categoryKey = 'fees-and-premiums';
    const categoryName = 'Fees and Premiums';

    // Ensure category exists
    if (!categorizedData[categoryKey]) {
        const data: IPlanComparisonSheetData = {
            rows: [],
            rowsLookup: {},
            columns: {},
            categoryName: categoryName
        };
        addBaseColumns(data);
        categorizedData[categoryKey] = data;
    }

    const data = categorizedData[categoryKey]!;

    // Add plan column if not exists
    if (!data.columns[columnName]) {
        data.columns[columnName] = {
            attributeId: '',
            order: 0,
            width: 35,
            index: Object.keys(data.columns).length,
            fieldName: columnName,
            hidden: false,
            name: columnName,
            dataType: EColumnExtractDataTypes.Default,
        };
    }

    // Process each fee/premium
    for (const entity of feesAndPremiums) {
        await processEntityFields(plan, columnName, entity, data, config, "feesAndPremiums");
    }
}

async function processEntityFields(
    plan: FAM_IPlan,
    columnName: string,
    entity: FAM_IBenefit,
    data: IPlanComparisonSheetData,
    config: any,
    context: "plan" | "benefit" | "feesAndPremiums"
): Promise<void> {
    const fieldsByMasterAttributeId = FieldsToLookupByMasterAttributeId(config, entity.fields);
    const tiers = entity.tiers ? ArrayToLookup(entity.tiers) : {};
    const prevKeyParts = [context, 'fields'];

    Object.values(fieldsByMasterAttributeId)
        .sort((a, b) => a[0].index - b[0].index)
        .forEach(eachField => {
            const eachFieldFirst = eachField[0];
            eachFieldFirst.field.values.forEach((eachFieldValue) => {
                const keyParts: string[] = [...prevKeyParts];

                if (eachFieldFirst.masterAttribute.bcbs_config_masterattributeid) {
                    keyParts.push(eachFieldFirst.masterAttribute.bcbs_config_masterattributeid);
                }

                if (eachFieldValue.tierRef?.id && tiers[eachFieldValue.tierRef.id]) {
                    keyParts.push(tiers[eachFieldValue.tierRef.id].bcbs_name!);
                }

                if (eachFieldValue.type) {
                    keyParts.push(eachFieldValue.type);
                }

                const key = keyParts.join('/');
                let row = data.rowsLookup[key];

                if (!row) {
                    row = {
                        namespace: keyParts.join('/'),
                        label: calcFieldName(eachFieldFirst.field, eachFieldValue, eachFieldFirst.configAttribute, tiers),
                        tab: eachFieldFirst.tab.bcbs_name,
                        section: eachFieldFirst.section.bcbs_name,
                        _config: eachFieldFirst,
                        _index: data.rows.length,
                        _category: context
                    };
                    data.rows.push(row);
                    data.rowsLookup[key] = row;
                }

                const controlType = eachFieldFirst.masterAttribute.bcbs_attributetypecode as EControlType;
                if (eachFieldValue.value) {
                    row[columnName] = friendlyValue(controlType, eachFieldValue.value);

                    if (controlType === EControlType.date) {
                        row[columnName] = {
                            value: friendlyValue(controlType, eachFieldValue.value),
                            format: { type: 'date' },
                            toString: () => friendlyValue(controlType, eachFieldValue.value)
                        };
                    }
                }
            });
        });
}

async function processGroupNumbers(
    plan: FAM_IPlan,
    columnName: string,
    groupNumbers: FAM_IGroupNumber[],
    data: IPlanComparisonSheetData
): Promise<void> {
    groupNumbers?.forEach(groupNumber => {
        const key = `groupnumber/${groupNumber.groupNumber}`;
        let row = data.rowsLookup[key];

        if (!row) {
            row = {
                label: `Group Number/${groupNumber.groupNumber}`,
                _index: data.rows.length,
                namespace: 'Group Number',
                _category: 'group-numbers'
            };
            data.rows.push(row);
            data.rowsLookup[key] = row;
        }

        row[columnName] = groupNumber.groupNumber;
    });
}

function addStatusColumns(data: IPlanComparisonSheetData): void {
    // Add change tracking columns
    data.columns['changeValues'] = {
        attributeId: '',
        order: 0,
        width: 15,
        index: Object.keys(data.columns).length,
        fieldName: 'changeValues',
        hidden: false,
        name: 'Change Values',
        dataType: EColumnExtractDataTypes.Default,
    };

    data.columns['status'] = {
        attributeId: '',
        order: 0,
        width: 13,
        index: Object.keys(data.columns).length,
        fieldName: 'status',
        hidden: false,
        name: 'Status',
        dataType: EColumnExtractDataTypes.Default,
    };
}

const addBaseColumns = (data: IPlanComparisonSheetData) => {


    data.columns['namespace'] = {
        attributeId: '',
        order: 0,
        width: 35,
        index: 0,
        fieldName: 'namespace',
        hidden: true,
        name: 'Namespace',
        dataType: EColumnExtractDataTypes.Default,
    };

    data.columns['tab'] = {
        attributeId: '',
        order: 1,
        width: 35,
        index: 1,
        fieldName: 'tab',
        hidden: false,
        name: 'Tab',
        dataType: EColumnExtractDataTypes.Default,
    };

    data.columns['section'] = {
        attributeId: '',
        order: 2,
        width: 35,
        index: 2,
        fieldName: 'section',
        hidden: false,
        name: 'Section',
        dataType: EColumnExtractDataTypes.Default,
    };

    data.columns['label'] = {
        attributeId: '',
        order: 3,
        width: 40,
        index: 3,
        fieldName: 'label',
        hidden: false,
        name: 'Field',
        dataType: EColumnExtractDataTypes.Default,
    };

};

const processRowChanges = (data: IPlanComparisonSheetData, options: IPlanComparisonOptions): void => {
    const excludeKeys: string[] = ['namespace', 'tab', 'section', 'label', 'status', 'changeValues',];
    const comparableColumns = Object.values(data.columns)
        .filter(col => !excludeKeys.includes(col.fieldName));

    data.rows.forEach(row => {
        row['status'] = unchanged;

        const rowDistinctValues: Record<string, any> = {};
        comparableColumns.forEach(column => {
            row[column.fieldName] = row[column.fieldName] ?? '-';
            const fieldValue = row[column.fieldName]?.toString() ?? '(undefined)';

            if (fieldValue === '-' && options.ignoreEmptyFields)
                return;

            rowDistinctValues[fieldValue] = fieldValue;
        });

        if (Object.keys(rowDistinctValues).length > 1) {
            row['status'] = changeFormat;
            row['changeValues'] = Object.values(rowDistinctValues)
                .sort((a, b) => a.localeCompare(b))
                .join(',\n');
        }

        // Clean up internal properties
        delete row._config;
        delete row._property;
        delete row._category;
    });

    if (options.showOnlyChanges) {
        debugger
        data.rows = data.rows.filter(row => {
            return row['status'] !== unchanged;
        });
    }

}

async function generateCategorizedDataset(
    categorizedData: IPlanComparisonCategorizedData,
    dataSource: AppDataSource,
    options: IPlanComparisonOptions
): Promise<any> {
    const tables: any[] = [];

    // Process each category
    Object.values(categorizedData).forEach((data, categoryKey) => {
        // Add status columns and process changes
        addStatusColumns(data);
        processRowChanges(data, options);

        // Create columns array sorted by index
        const columnsArray = Object.values(data.columns)
            .sort((a, b) => a.index - b.index);

        // Add table to dataset
        tables.push({
            columns: columnsArray,
            data: data.rows,
            name: data.categoryName
        });
    });

    const dataset: IDatasetExtract = {
        name: '',
        tables: tables,
        dropDownTables: null,
        styles: { ...ExcelStyles, fontSize: 10 }
    };

    return await dataSource.getGenerateExcelExtract(dataset);
}